import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { acquireActivitySocket } from "../lib/activitySocketPool";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { BANDS, MODES, isFrequencyValidForBand, isBandModeBlocked, normalizeFrequencyInput, formatFrequencyDisplay, colors, fontMono, s } from "../lib/theme";
import ChatPanel from "./ChatPanel";
import { useIsMobile } from "../lib/useIsMobile";
import OtaMobile from "./OtaMobile";

const DIGITAL_MODES = new Set(["FT8", "FT4", "FT2", "RTTY", "PSK31"]);

export default function Matrix() {
  const { activityId } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();
  const socketRef = useRef(null);

  const [activity, setActivity] = useState(null);
  const [occupancy, setOccupancy] = useState([]); // [{band, mode, callsign, frequency, published_to_cluster, user_id}]
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [chatData, setChatData] = useState(null);
  const [connError, setConnError] = useState(null);
  const [freqDrafts, setFreqDrafts] = useState({}); // borrador local del input de frecuencia por celda
  const [actionError, setActionError] = useState(null);
  const [clusterTranscript, setClusterTranscript] = useState(null);
  const [clusterConfirmed, setClusterConfirmed] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const isMobile = useIsMobile();
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    api.getActivity(activityId).then(setActivity).catch(() => {});
  }, [activityId]);

  // Solo se muestran las bandas/modos que el organizador marcó al configurar
  // esta actividad concreta — nunca el catálogo completo de la app.
  const activeBands = activity ? BANDS.filter((b) => (activity.bands || []).includes(b.id)) : [];
  const activeModes = activity ? MODES.filter((m) => (activity.modes || []).includes(m)) : [];


  useEffect(() => {
    const { socket, release } = acquireActivitySocket(activityId, session.token, (res) => {
      if (res?.error) {
        setConnError(res.error);
        return;
      }
      setOccupancy(res.occupancy);
      setIsOrganizer(res.isOrganizer);
      setChatData(res.chat || null);
    });
    socketRef.current = socket;

    socket.on("matrix_update", (rows) => setOccupancy(rows));
    socket.on("connect_error", () => setConnError("No se pudo conectar al servidor en tiempo real"));

    return () => {
      socket.off("matrix_update");
      socket.off("connect_error");
      release();
    };
  }, [activityId, session.token]);

  const cellFor = useCallback(
    (band, mode) => occupancy.find((o) => o.band === band && o.mode === mode) || null,
    [occupancy]
  );

  const myCall = session.user.callsign;
  const activityFinished = activity?.ends_at && new Date(activity.ends_at) < new Date();

  const claim = (band, mode) => {
    setActionError(null);
    socketRef.current.emit("claim_cell", { activityId, band, mode }, (res) => {
      if (res?.error) setActionError(res.error);
    });
  };

  const release = (band, mode) => {
    socketRef.current.emit("release_cell", { activityId, band, mode }, () => {});
  };

  const forceRelease = (band, mode) => {
    socketRef.current.emit("force_release", { activityId, band, mode }, (res) => {
      if (res?.error) setActionError(res.error);
    });
  };

  const submitFrequency = (band, mode) => {
    const key = `${band}-${mode}`;
    // Sin borrador = no hay nada nuevo que guardar. Pasa al pulsar OK: al
    // quitar el foco del campo ya se guarda, y el clic en OK dispara un
    // segundo guardado — que, sin esta comprobación, llegaba sin valor y
    // dejaba la frecuencia en blanco sin avisar.
    if (freqDrafts[key] === undefined) return;
    const value = normalizeFrequencyInput(freqDrafts[key]);
    if (!isFrequencyValidForBand(band, value, mode)) {
      setActionError("Frecuencia fuera del rango de la banda");
      return;
    }
    socketRef.current.emit("set_frequency", { activityId, band, mode, frequency: value }, (res) => {
      if (res?.error) { setActionError(res.error); return; }
      // Se limpia el borrador local al guardar bien — si no, el campo se
      // quedaba mostrando para siempre el texto tal cual lo escribiste la
      // primera vez, en vez de pasar a mostrar el valor ya guardado (y
      // bien formateado, con la coma del decimal extra si lo hay).
      setFreqDrafts((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    });
  };

  const publish = (band, mode) => {
    socketRef.current.emit("publish_cluster", { activityId, band, mode }, (res) => {
      if (res?.error) setActionError(res.error);
      setClusterTranscript(res?.transcript || null);
      setClusterConfirmed(res?.confirmed ?? null);
    });
  };

  if (connError) {
    return (
      <div style={s.page}>
        <div style={s.errorBox}>{connError}</div>
        <Link to="/actividades" style={{ color: colors.amber }}>← Volver a tus actividades</Link>
      </div>
    );
  }

  // Actividad OTA (SOTA/POTA/IOTA/DME...), vista desde el móvil: interfaz
  // simplificada aparte, no la Matriz completa. Desde escritorio, siempre
  // se ve la Matriz normal, sea OTA o no — el cambio es solo para móvil.
  if (activity?.is_ota && isMobile) {
    return (
      <OtaMobile
        activity={activity} activityId={activityId} socket={socketRef.current}
        occupancy={occupancy} session={session} activeBands={activeBands} activeModes={activeModes}
      />
    );
  }

  return (
    <div style={{ ...s.page, maxWidth: 1680 }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "flex-start",
        flexDirection: isMobile ? "column" : "row", gap: isMobile ? 14 : 0, marginBottom: 18,
      }}>
        <div>
          <div style={s.eyebrow}>ACTIVIDAD EN CURSO</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {activity?.logo_url && (
              <img src={activity.logo_url} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover", border: `1px solid ${colors.lineStrong}` }} />
            )}
            <h1 style={s.h1}>{activity?.name || "Matriz banda / modo"}</h1>
            {activity?.callsign && (
              <span style={{
                fontFamily: fontMono, fontSize: 14, fontWeight: 700, color: colors.amber,
                background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "3px 9px",
              }}>
                {activity.callsign}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Link to={`/actividades/${activityId}/estadisticas`} style={{ ...s.btnGhost, textDecoration: "none" }}>
            Estadísticas
          </Link>
          {(isOrganizer
            ? occupancy.some((o) => DIGITAL_MODES.has(o.mode))
            : occupancy.some((o) => o.callsign === myCall && DIGITAL_MODES.has(o.mode))
          ) && (
            <Link
              to={`/actividades/${activityId}/propagacion`}
              style={{ ...s.btnGhost, textDecoration: "none", background: colors.green, color: "#08130c", border: "none", fontWeight: 700 }}
            >
              Ver propagación
            </Link>
          )}
          <button
            onClick={() => api.downloadExport(activityId, "adif", "mine", activity?.name || "log").catch((err) => setActionError(err.message))}
            style={s.btnGhost}
          >
            Descargar mi log
          </button>
          {isOrganizer && (
            <button
              onClick={() => api.downloadExport(activityId, "adif", "all", activity?.name || "log").catch((err) => setActionError(err.message))}
              style={s.btnGhost}
            >
              Descargar log completo
            </button>
          )}
          <button onClick={() => setShowImportModal(true)} style={s.btnGhost}>
            Subir log (ADIF)
          </button>
          <span style={{ fontFamily: fontMono, fontSize: 13, background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "6px 12px" }}>
            {myCall}
          </span>
        </div>
      </div>

      {showImportModal && (
        <ImportAdifModal
          activityId={activityId}
          isOrganizer={isOrganizer}
          onClose={() => setShowImportModal(false)}
        />
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: 16 }}>
        <Legend color={colors.green} bg={colors.greenBg} label="Libre" />
        <Legend color={colors.redBorder} bg={colors.redBg} label="Ocupada" />
        <Legend color={colors.amber} bg={colors.redBg} label="Ocupada por ti" />
      </div>

      {actionError && <div style={s.errorBox}>{actionError}</div>}

      {clusterTranscript && clusterTranscript.length > 0 && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ fontSize: 12, color: colors.textFaint, cursor: "pointer" }}>
            Ver conversación con el DX Cluster (último intento de publicar)
            {clusterConfirmed === true && <span style={{ color: colors.green }}> — confirmado por el nodo ✓</span>}
            {clusterConfirmed === false && <span style={{ color: colors.amber }}> — enviado, pero sin confirmación explícita del nodo</span>}
          </summary>
          <div style={{
            marginTop: 8, padding: 10, borderRadius: 6, background: colors.panel,
            border: `1px solid ${colors.lineStrong}`, fontFamily: fontMono, fontSize: 11.5,
            maxHeight: 240, overflowY: "auto", whiteSpace: "pre-wrap",
          }}>
            {clusterTranscript.map((line, i) => (
              <div key={i} style={{
                color: line.dir.startsWith("hex") ? "#7c8a99" : line.dir === "enviado" ? colors.amber : line.dir === "info" ? colors.textFaint : colors.textDim,
                fontSize: line.dir.startsWith("hex") ? 10.5 : 11.5,
                marginBottom: 3,
              }}>
                [{line.dir}] {line.text}
              </div>
            ))}
          </div>
        </details>
      )}

      {activity && (activeBands.length === 0 || activeModes.length === 0) && (
        <div style={s.errorBox}>
          Esta actividad todavía no tiene bandas y modos configurados — pide al organizador que los añada en la ficha de la actividad.
        </div>
      )}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>

      <div style={{ overflow: "auto", maxHeight: 640, border: `1px solid ${colors.line}`, borderRadius: 10, background: colors.panel }}>
        <div style={{ padding: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: `92px repeat(${activeModes.length}, minmax(92px, 1fr))`, gap: 8, minWidth: 92 + activeModes.length * 92 }}>
          <div style={{ position: "sticky", top: 0, zIndex: 3, background: colors.panel, boxShadow: `0 8px 0 0 ${colors.panel}` }} />
          {activeModes.map((m) => (
            <div key={m} style={{
              textAlign: "center", fontSize: 13.5, fontWeight: 700, color: colors.textDim, padding: "8px 4px",
              position: "sticky", top: 0, zIndex: 3, background: colors.panel, boxShadow: `0 8px 0 0 ${colors.panel}`,
            }}>{m}</div>
          ))}

          {activeBands.map((band) => (
            <React.Fragment key={band.id}>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", fontSize: 13.5, fontWeight: 700, color: colors.text, padding: "4px 6px" }}>
                <div>{band.label}</div>
                <div style={{ fontFamily: fontMono, fontSize: 9.5, color: colors.textFaint }}>
                  {band.range ? `${band.range[0].toFixed(3)}–${band.range[1].toFixed(3)}` : "sin rango fijo"}
                </div>
              </div>

              {activeModes.map((mode) => {
                const cell = cellFor(band.id, mode);
                const key = `${band.id}-${mode}`;
                const mine = cell?.callsign === myCall;

                if (isBandModeBlocked(band.id, mode)) {
                  return (
                    <div
                      key={key}
                      title={`${mode} no está permitido en ${band.label} — restricción del plan de bandas, no configurable`}
                      style={{
                        minHeight: 108, borderRadius: 7, border: `1px dashed ${colors.lineStrong}`,
                        background: "repeating-linear-gradient(45deg, #12161c, #12161c 8px, #171c23 8px, #171c23 16px)",
                        display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.55,
                      }}
                    >
                      <span style={{ fontSize: 10.5, color: colors.textFaint, textAlign: "center", padding: "0 6px" }}>No permitido</span>
                    </div>
                  );
                }

                if (!cell) {
                  // La actividad ya finalizó: reclamar celdas nuevas ya no
                  // tiene sentido (nadie va a "estar trabajando en vivo"
                  // un concurso terminado) — para el organizador, esta
                  // celda libre lleva directo al Log, para poder meter a
                  // mano algún contacto que le hayan pasado después de
                  // cerrar (por teléfono, etc.), sin pasar por reclamar.
                  if (activityFinished && isOrganizer) {
                    return (
                      <Link
                        key={key}
                        to={`/actividades/${activityId}/log?band=${band.id}&mode=${mode}&freq=`}
                        style={{
                          minHeight: 108, borderRadius: 7, border: `1.5px dashed ${colors.textFaint}`, background: colors.panel,
                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none",
                        }}
                        title="Actividad finalizada — registrar a mano"
                      >
                        <span style={{ color: colors.textFaint, fontSize: 11, fontWeight: 700, textAlign: "center", padding: "0 6px" }}>
                          Registrar a mano
                        </span>
                      </Link>
                    );
                  }
                  return (
                    <button
                      key={key}
                      onClick={() => claim(band.id, mode)}
                      style={{
                        minHeight: 108, borderRadius: 7, border: `1.5px solid ${colors.green}`, background: colors.greenBg,
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <span style={{ color: colors.green, fontSize: 20, fontWeight: 700, opacity: 0.6 }}>+</span>
                    </button>
                  );
                }

                return (
                  <div key={key} style={{
                    minHeight: 108, borderRadius: 7, border: `1.5px solid ${mine ? colors.amber : colors.redBorder}`,
                    background: colors.redBg, padding: "7px 8px", display: "flex", flexDirection: "column", gap: 4,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontFamily: fontMono, fontSize: 13.5, fontWeight: 700, color: "#f4d9d9" }}>
                        {!mine && "🔒 "}{cell.callsign}
                      </span>
                      {cell.published_to_cluster && <span title="Publicado en el cluster">📡</span>}
                    </div>

                    {mine ? (
                      <>
                        <div style={{ display: "flex", gap: 4 }}>
                          <input
                            value={freqDrafts[key] ?? (cell.frequency ? formatFrequencyDisplay(cell.frequency) : "")}
                            onChange={(e) => setFreqDrafts((p) => ({ ...p, [key]: e.target.value }))}
                            onBlur={() => submitFrequency(band.id, mode)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                submitFrequency(band.id, mode);
                              }
                            }}
                            placeholder={band.range ? band.range[0].toFixed(3) : "frecuencia (libre)"}
                            style={{
                              background: "#171016", border: `1px solid ${colors.lineStrong}`, borderRadius: 5,
                              color: colors.text, fontFamily: fontMono, fontSize: 13.5, padding: "6px 8px", width: "100%",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => submitFrequency(band.id, mode)}
                            title="Confirmar frecuencia"
                            style={{
                              background: colors.green, color: colors.void, border: "none", borderRadius: 5,
                              fontSize: 12, fontWeight: 700, padding: "0 10px", cursor: "pointer", flexShrink: 0,
                            }}
                          >
                            OK
                          </button>
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: "auto" }}>
                          {cell.frequency && (
                            <Link
                              to={`/actividades/${activityId}/log?band=${band.id}&mode=${mode}&freq=${cell.frequency}`}
                              style={{ fontSize: 11, fontWeight: 700, color: colors.void, background: colors.green, border: "none", borderRadius: 4, padding: "5px 8px", textDecoration: "none" }}
                            >
                              Ir al Log →
                            </Link>
                          )}
                          {cell.frequency && !cell.published_to_cluster && (
                            <button onClick={() => publish(band.id, mode)} style={miniBtnAmber}>Publicar</button>
                          )}
                          <button onClick={() => release(band.id, mode)} style={miniBtnGhost}>Liberar</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontFamily: fontMono, fontSize: 12.5, color: "#c98f8f" }}>
                          {cell.frequency ? `${formatFrequencyDisplay(cell.frequency)} MHz` : "sin frecuencia"}
                        </div>
                        {isOrganizer && (
                          <button onClick={() => forceRelease(band.id, mode)} style={miniBtnRed}>Forzar liberación</button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        </div>
      </div>

      <p style={{ marginTop: 14, fontSize: 12, color: colors.textFaint }}>
        La celda solo se libera manualmente, al cerrar sesión o si el organizador la fuerza — salir hacia el Log no la libera.
      </p>
      </div>

      {chatData && !isMobile && (
        <div style={{ position: "sticky", top: 20 }}>
          <ChatPanel
            socket={socketRef.current}
            activityId={activityId}
            initialChat={chatData}
            myUserId={session.user.id}
          />
        </div>
      )}
      </div>

      {/* Móvil: el chat no cabe al lado de la cuadrícula — se abre como
          panel flotante con un botón, en vez de solaparse encima. */}
      {chatData && isMobile && (
        <>
          <button
            onClick={() => setChatOpen(true)}
            style={{
              position: "fixed", right: 16, bottom: 16, zIndex: 40,
              background: colors.amber, color: "#1a1200", border: "none", borderRadius: 999,
              padding: "12px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
            }}
          >
            💬 Chat
          </button>
          {chatOpen && (
            <div style={{
              position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.6)",
              display: "flex", flexDirection: "column", justifyContent: "flex-end",
            }} onClick={() => setChatOpen(false)}>
              <div style={{ maxHeight: "80vh" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 6px 0" }}>
                  <button
                    onClick={() => setChatOpen(false)}
                    style={{ background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, color: colors.text, borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}
                  >
                    Cerrar ✕
                  </button>
                </div>
                <ChatPanel
                  socket={socketRef.current}
                  activityId={activityId}
                  initialChat={chatData}
                  myUserId={session.user.id}
                  containerStyle={{ width: "100%", minWidth: 0, height: "70vh", borderRadius: "12px 12px 0 0" }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Legend({ color, bg, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${color}`, background: bg, display: "inline-block" }} />
      <span style={{ fontSize: 12.5, color: colors.textDim }}>{label}</span>
    </div>
  );
}

function ImportAdifModal({ activityId, isOrganizer, onClose }) {
  const [file, setFile] = useState(null);
  const [operators, setOperators] = useState(null);
  const [operatorId, setOperatorId] = useState(""); // "" = subir para uno mismo
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOrganizer) {
      api.listOperators(activityId).then(setOperators).catch(() => setOperators([]));
    }
  }, [activityId, isOrganizer]);

  const doPreview = async () => {
    if (!file) return;
    setError(null);
    setLoading(true);
    try {
      const data = await api.importAdif(activityId, file, "preview", operatorId || undefined);
      setPreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const doConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await api.importAdif(activityId, file, "confirm", operatorId || undefined);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div style={{ width: 420, background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, borderRadius: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: `1px solid ${colors.line}` }}>
          <span style={{ fontSize: 15.5, fontWeight: 700, color: colors.text }}>Subir log (ADIF)</span>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: colors.textDim, cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        <div style={{ padding: "18px 22px" }}>
          {result ? (
            <>
              <p style={{ fontSize: 14, color: colors.green, fontWeight: 600 }}>✓ Importación completada</p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.textDim, lineHeight: 1.7 }}>
                <li><b style={{ color: colors.text }}>{result.inserted}</b> QSOs importados</li>
                <li>{result.duplicates} descartados por estar duplicados</li>
                <li>{result.unauthorized} descartados por banda/modo no autorizados en esta actividad</li>
                <li>{result.problems} descartados por datos incompletos en el archivo</li>
              </ul>
              <button onClick={onClose} style={{ ...s.btnPrimary, marginTop: 18 }}>Cerrar</button>
            </>
          ) : (
            <>
              {isOrganizer && (
                <div style={{ marginBottom: 14 }}>
                  <label style={s.label}>SUBIR EN NOMBRE DE</label>
                  <select
                    value={operatorId}
                    onChange={(e) => { setOperatorId(e.target.value); setPreview(null); }}
                    style={{ ...s.input, background: colors.panel }}
                  >
                    <option value="">Mí mismo</option>
                    {operators?.map((op) => (
                      <option key={op.id} value={op.id} style={{ background: colors.panelRaised, color: colors.text }}>
                        {op.callsign} — {op.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <label style={s.label}>ARCHIVO ADIF (.adi)</label>
              <input
                type="file"
                accept=".adi,.adif"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                style={{ fontSize: 13, color: colors.textDim, marginBottom: 16 }}
              />

              {error && <div style={s.errorBox}>{error}</div>}

              {preview && (
                <div style={{ ...s.card, marginBottom: 16, background: colors.panel }}>
                  <p style={{ fontSize: 13, color: colors.text, margin: "0 0 8px" }}>
                    <b>{preview.total}</b> contactos encontrados en el archivo:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.textDim, lineHeight: 1.7 }}>
                    <li><b style={{ color: colors.green }}>{preview.valid}</b> se importarían correctamente</li>
                    <li>{preview.duplicates} ya están duplicados</li>
                    <li>{preview.unauthorized} en banda/modo no autorizados en esta actividad</li>
                    <li>{preview.problems} con datos incompletos (sin indicativo, fecha, etc.)</li>
                  </ul>
                  {preview.entities?.length > 0 && (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ fontSize: 12, color: colors.textFaint, cursor: "pointer" }}>
                        {preview.entities.length} entidad{preview.entities.length === 1 ? "" : "es"} DXCC entre los que se importarían — comparar con tu propio log
                      </summary>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {preview.entities.map((e) => (
                          <span key={e.entity} style={{
                            fontSize: 11.5, color: colors.textDim, background: colors.panelRaised,
                            border: `1px solid ${colors.lineStrong}`, borderRadius: 4, padding: "3px 8px",
                          }}>
                            {e.entity} ({e.count})
                          </span>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${colors.lineStrong}`, color: colors.textDim, borderRadius: 6, fontWeight: 700, fontSize: 13, padding: "9px 16px", cursor: "pointer" }}>
                  Cancelar
                </button>
                {!preview ? (
                  <button onClick={doPreview} disabled={!file || loading} style={{ ...s.btnGhost, opacity: !file || loading ? 0.5 : 1 }}>
                    {loading ? "Analizando…" : "Vista previa"}
                  </button>
                ) : (
                  <button onClick={doConfirm} disabled={loading || preview.valid === 0} style={{ ...s.btnPrimary, opacity: loading || preview.valid === 0 ? 0.5 : 1 }}>
                    {loading ? "Importando…" : `Confirmar importación (${preview.valid})`}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const miniBtnAmber = { display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: "#0a0e13", background: "#f5a623", border: "none", borderRadius: 4, padding: "5px 8px", cursor: "pointer" };
const miniBtnGhost = { display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: "#dce4ec", background: "transparent", border: "1px solid #3f4c58", borderRadius: 4, padding: "5px 8px", cursor: "pointer" };
const miniBtnRed = { fontSize: 11, fontWeight: 700, color: "#0a0e13", background: "#ef4444", border: "none", borderRadius: 4, padding: "5px 8px", cursor: "pointer", marginTop: "auto" };
